import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    CircularProgress,
    Alert,
    Box,
    Chip
} from '@mui/material';
import { SmsService } from '../../service/smsService';
import { SmsTemplateService } from '../../service/smsTemplateService';

export default function SendSmsDialog({ 
    open, 
    recipient,           // Generic recipient object with: recipientId, phone, name, email
    entityId,            // ID of the related entity (appointmentId, invoiceId, etc)
    entityType = 'appointment',  // Type of entity ('appointment', 'invoice', etc)
    defaultCommunicationType = 'appointment_reminder',
    onClose, 
    onSuccess 
}) {
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [communicationType, setCommunicationType] = useState(defaultCommunicationType);

    const communicationTypes = [
        'appointment_reminder',
        'appointment_confirmation',
        'order_ready',
        'contact_lens_renewal',
        'eye_exam_recall',
        'follow_up',
        //'general'
    ];

    // Load templates on mount
    useEffect(() => {
        if (open) {
            loadTemplates();
            setError('');
            setMessage('');
            setSelectedTemplate('');
        }
    }, [open]);

    const loadTemplates = async () => {
        try {
            const response = await SmsTemplateService.getTemplates();
            setTemplates(response.data || []);
        } catch (err) {
            console.error('Failed to load templates:', err);
            // Fallback to empty templates array
            setTemplates([]);
        }
    };

    /**
     * Load template by combining header + body + footer
     * Backend will handle token replacement when sending
     */
    const handleSelectTemplate = (templateId) => {
        const template = templates.find(t => t.id === templateId);
        if (template) {
            // Combine header, body, and footer with proper spacing
            let fullMessage = '';
            if (template.header) {
                fullMessage += template.header + '\n\n';
            }
            fullMessage += template.body;
            if (template.footer) {
                fullMessage += '\n\n' + template.footer;
            }
            
            // Use raw template with tokens intact - backend will replace them
            setMessage(fullMessage);
            setSelectedTemplate(templateId);
        }
    };

    const handleSend = async () => {
        if (!message.trim()) {
            setError('Message cannot be empty');
            return;
        }

        if (!recipient?.recipientId) {
            setError('Recipient ID is missing');
            return;
        }

        if (!recipient?.phone) {
            setError('Recipient phone number is missing');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Prepare payload for /sms/send endpoint
            // Backend will handle token replacement
            const payload = {
                customerId: recipient.recipientId,
                toNumber: recipient.phone,
                message: message, // Raw message with tokens - backend will replace them
                appointmentId: entityType === 'appointment' ? entityId : undefined,
                invoiceId: entityType === 'invoice' ? entityId : undefined,
                communicationType: communicationType,
                // Track replies for appointment reminders/confirmations
                expectReply: ['appointment_reminder', 'appointment_confirmation'].includes(communicationType)
            };

            const response = await SmsService.sendSms(payload);

            if (response?.code === 0) {
                onSuccess?.('SMS sent successfully');
                onClose();
            } else {
                setError(response?.message || 'Failed to send SMS');
            }
        } catch (err) {
            setError(err.message || 'Failed to send SMS');
            console.error('SMS send error:', err);
        } finally {
            setLoading(false);
        }
    };

    const characterCount = message.length;
    const smsCount = Math.ceil(characterCount / 160);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Send SMS to {recipient?.name}</DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Stack spacing={2}>
                    {/* Recipient info */}
                    <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            To: {recipient?.phone}
                        </Typography>
                        {recipient?.email && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                Email: {recipient.email}
                            </Typography>
                        )}
                    </Box>

                    {/* Error alert */}
                    {error && <Alert severity="error">{error}</Alert>}

                    {/* Communication type */}
                    <FormControl fullWidth size="small">
                        <InputLabel>Communication Type</InputLabel>
                        <Select
                            value={communicationType}
                            onChange={(e) => setCommunicationType(e.target.value)}
                            label="Communication Type"
                        >
                            {communicationTypes.map(type => (
                                <MenuItem key={type} value={type}>
                                    {type.replace(/_/g, ' ')}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Template selector */}
                    <FormControl fullWidth size="small">
                        <InputLabel>Use Template (Optional)</InputLabel>
                        <Select
                            value={selectedTemplate}
                            onChange={(e) => handleSelectTemplate(e.target.value)}
                            label="Use Template (Optional)"
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {templates.map(template => (
                                <MenuItem key={template.id} value={template.id}>
                                    {template.templateName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Message textarea */}
                    <TextField
                        multiline
                        rows={5}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Enter your message here..."
                        fullWidth
                        disabled={loading}
                        variant="outlined"
                    />

                    {/* Character count and SMS count */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {characterCount} characters
                        </Typography>
                        <Chip
                            label={`${smsCount} SMS${smsCount !== 1 ? 's' : ''}`}
                            size="small"
                            variant={smsCount > 5 ? 'filled' : 'outlined'}
                            color={smsCount > 5 ? 'warning' : 'default'}
                        />
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSend}
                    variant="contained"
                    disabled={loading || !message.trim()}
                    sx={{ minWidth: 100 }}
                >
                    {loading ? <CircularProgress size={24} /> : 'Send SMS'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
